import {useAsyncFn, useEffectOnce} from "react-use";
import {PageSearchResponse, PageSearchResult} from "navigation-common/types/confluence-types";
import {confluenceService} from "../../api/ConfluenceService";
import React, {memo, useState} from "react";
import {PARAM_APP_MENUITEM_CQL, PARAM_APP_MENUITEM_IMAGEURL, PARAM_APP_MENUITEM_NAME, PARAM_APP_MENUITEM_NEWTAB} from "navigation-common/types/pages-constants";
import "./styles/CqlModal.scss";
import Button, {IconButton} from "@atlaskit/button/new";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import {SkeletonItem} from '@atlaskit/menu';
import NoResultsSvg from "../../assets/img/NoResult.svg";
import BadConfigSvg from "../../assets/img/BadConfig.svg";

export const CqlModal = () => {
    const [pageSearchResponseList, setPageSearchResponseList] = useState<PageSearchResponse>();
    const [modalTitle, setModalTitle] = useState<string>("");
    const [openLinksInNewTab, setOpenLinksInNewTab] = useState<boolean>(false);
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

    const [runCqlState, runCql] = useAsyncFn(async (cqlString: string, nextUrl?: string) => {
        if (!cqlString) {
            console.warn("No CQL string to run");
            return;
        }

        const searchResult = await confluenceService.searchByCql(cqlString, nextUrl);
        setPageSearchResponseList(searchResult);
    }, []);

    useEffectOnce(() => {
        AP.dialog.getCustomData((params: any) => {
            setModalTitle(params[PARAM_APP_MENUITEM_NAME]);
            setOpenLinksInNewTab(params[PARAM_APP_MENUITEM_NEWTAB]);
            setImageUrl(params[PARAM_APP_MENUITEM_IMAGEURL]);
            runCql(params[PARAM_APP_MENUITEM_CQL]);
        });
    });

    const onCloseModalClicked = () => {
        AP.dialog.close();
    }

    const renderLoadingSkeleton = () => {
        return (
            <>
                <SkeletonItem isShimmering={true}/>
                <SkeletonItem isShimmering={true}/>
                <SkeletonItem isShimmering={true}/>
                <SkeletonItem isShimmering={true}/>
            </>
        );
    }

    const renderResultsByState = () => {
        if (runCqlState.loading) {
            return renderLoadingSkeleton();
        }

        if (runCqlState.error) {
            return <EmptySearchResultsState reason={EmptyStateReason.ERROR}/>;
        }

        if (pageSearchResponseList && pageSearchResponseList?.size > 0) {
            return (
                <div className={"resultListContainer"}>
                    <ul className={"resultList"}>
                        <PageResultsPaginator searchResponse={pageSearchResponseList} openLinksInNewTab={openLinksInNewTab}/>
                    </ul>
                </div>
            );
        }

        return <EmptySearchResultsState reason={EmptyStateReason.NO_RESULTS}/>;
    }

    return (
        <div className="modalContainer">
            <div className={"modalHeaderContainer"}>
                {imageUrl && <div className={"headerBgImage"} style={{backgroundImage: `url(${imageUrl})`}}></div>}
                <div className={"modalHeader"}>
                    <h3 style={imageUrl ? {marginTop: "78px"} : {}}>{modalTitle}</h3>
                    <div className={"closeBtn"}>
                        <IconButton icon={CrossIcon} label={"close"} onClick={onCloseModalClicked} spacing="compact" appearance="default"/>
                    </div>
                </div>
            </div>
            <div className={"modalBody"} style={imageUrl ? {marginTop: "132px"} : {}}>
                {renderResultsByState()}
            </div>
        </div>
    );
}

const PageResultsPaginator = (props: { searchResponse: PageSearchResponse, openLinksInNewTab: boolean }) => {
    const [nextPageSearchResponse, setNextPageSearchResponse] = useState<PageSearchResponse>();
    const [hideLoadNext, setHideLoadNext] = useState<boolean>(false);

    const [searchMoreState, searchMore] = useAsyncFn(async (nextUrl: string) => {
        if (!nextUrl) {
            console.log("No next URL to run");
            return;
        }

        const searchResult = await confluenceService.searchByCql("", nextUrl);
        setHideLoadNext(true);
        setNextPageSearchResponse(searchResult);
    }, []);

    return (
        <>
            <PageResults results={props.searchResponse.results} openLinksInNewTab={props.openLinksInNewTab}/>
            {props.searchResponse.next && !hideLoadNext &&
                <>
                    <li className={"loadMoreResults"}>
                        <Button isLoading={searchMoreState.loading} appearance="subtle" iconBefore={ArrowDownIcon}
                                onClick={() => searchMore(props.searchResponse.next ?? "")}>Show more</Button>
                    </li>
                </>
            }
            {nextPageSearchResponse && nextPageSearchResponse.results.length > 0 &&
                <PageResultsPaginator searchResponse={nextPageSearchResponse} openLinksInNewTab={props.openLinksInNewTab}/>
            }
        </>
    );
};

const PageResults = memo((props: { results: PageSearchResult[], openLinksInNewTab: boolean }) => {
    return (<>
            {props.results.map((result, i) => {
                return <li key={result.id + i} className={"linkItem"}>
                    <a href={result.link} target={props.openLinksInNewTab ? "_blank" : "_parent"}>{result.title}</a>
                </li>
            })}
        </>
    );
});

export const EmptySearchResultsState = (props: { reason: EmptyStateReason }) => {
    return (
        <div className={"emptyStateContainer"}>
            {props.reason.toString() === EmptyStateReason.NO_RESULTS.toString() && (
                <>
                    <img className={"empty-state-image"} src={NoResultsSvg} alt={"No results"}/>
                    <h3>No results were found</h3>
                    <div>Unfortunately, this query does not return any results.</div>
                </>
            )}
            {props.reason.toString() === EmptyStateReason.ERROR.toString() && (
                <>
                    <img className={"empty-state-image"} src={BadConfigSvg} alt={"Bad configuration"}/>
                    <h3>Bad configuration</h3>
                    <div>There seems to be an error in your CQL search. <br/>Change the search request or contact an administrator.</div>
                </>
            )}
        </div>
    );
};

enum EmptyStateReason {
    NO_RESULTS,
    ERROR
}

export const openCqlResultsDialog = async (menuItemName: string, cqlValue: string, openInNewTab: boolean, imageUrl: string | undefined) => {
    AP.dialog.create({
        key: 'cql-results-dialog',
        chrome: false, closeOnEscape: true, size: 'medium',
        customData: {
            [PARAM_APP_MENUITEM_CQL]: cqlValue,
            [PARAM_APP_MENUITEM_NAME]: menuItemName,
            [PARAM_APP_MENUITEM_NEWTAB]: openInNewTab,
            [PARAM_APP_MENUITEM_IMAGEURL]: imageUrl
        },
    }).on("close", () => {
        console.log("Dialog closed")
    });
};
